import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { expenseService } from '../services/expenseService';
import { categoryService } from '../services/categoryService';
import { accountService } from '../services/accountService';
import ExpenseList from './ExpenseList';
import AddExpense from './AddExpense';
import ImportCSV from './ImportCSV';
import ExpenseFilters from './ExpenseFilters';
import SimplePieChart from './SimplePieChart';
import BudgetTracker from './BudgetTracker';
import ChangePassword from './ChangePassword';
import OnboardingSlides from './OnboardingSlides';

function Home() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showImportCSV, setShowImportCSV] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [filters, setFilters] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadExpenses();
    loadCategories();
    loadAccounts();

    // Check if user should see tutorial
    if (user && !user.tutorial) {
      setShowTutorial(true);
    }
  }, [user]);

  const loadExpenses = async (currentFilters = filters) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem('user'));
      const data = await expenseService.getRecentExpenses(user.familyId, currentFilters);
      const dataBody = JSON.parse(data.body);
      const expenses = dataBody.expenses;
      setExpenses(expenses || []);
    } catch (error) {
      console.error('Failed to load expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      setLoadingCategories(true);
      // Load both hierarchical and flat categories
      const [hierarchicalData, flatData] = await Promise.all([
        categoryService.getCategories(),
        categoryService.getCategoriesFlat()
      ]);
      
      let hierarchicalCategories = hierarchicalData.categories || [];
      let flatCategories = flatData.categories || [];

      // If no categories exist, initialize defaults
      if (flatCategories.length === 0) {
        const defaultData = await categoryService.initializeDefaultCategories();
        // After initializing defaults, get both formats again
        const [newHierarchicalData, newFlatData] = await Promise.all([
          categoryService.getCategories(),
          categoryService.getCategoriesFlat()
        ]);
        hierarchicalCategories = newHierarchicalData.categories || [];
        flatCategories = newFlatData.categories || [];
      }

      // Create ordered list for dropdowns (parent categories followed by their subcategories)
      const orderedCategories = [];
      
      // Sort parent categories alphabetically
      hierarchicalCategories.sort((a, b) => a.name.localeCompare(b.name));
      
      hierarchicalCategories.forEach(parentCategory => {
        // Add parent category
        orderedCategories.push(parentCategory);
        
        // Add subcategories if they exist, sorted alphabetically
        if (parentCategory.subcategories && parentCategory.subcategories.length > 0) {
          const sortedSubcategories = [...parentCategory.subcategories].sort((a, b) => a.name.localeCompare(b.name));
          orderedCategories.push(...sortedSubcategories);
        }
      });

      setCategories(orderedCategories);
    } catch (error) {
      console.error('Failed to load categories:', error);
      // Fallback to hardcoded categories (also sorted)
      const fallbackCategories = [
        { name: 'Bills & Utilities', icon: '💡' },
        { name: 'Entertainment', icon: '🎬' },
        { name: 'Food & Dining', icon: '🍽️' },
        { name: 'Healthcare', icon: '🏥' },
        { name: 'Other', icon: '📦' },
        { name: 'Shopping', icon: '🛍️' },
        { name: 'Transportation', icon: '🚗' },
        { name: 'Travel', icon: '✈️' }
      ];
      setCategories(fallbackCategories);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadAccounts = async () => {
    try {
      const data = await accountService.getAccounts();
      setAccounts(data || []);
    } catch (error) {
      console.error('Failed to load accounts:', error);
    }
  };

  const handleAddExpense = async (expenseData) => {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      expenseData.familyId = user.familyId;
      expenseData.userId = user.id;
      const response = await expenseService.createExpense(expenseData);

      // Handle different response formats
      let newExpense;
      if (response.body) {
        const responseBody = JSON.parse(response.body);
        newExpense = responseBody.expense;
      } else {
        newExpense = response.expense || response;
      }

      if (newExpense) {
        // Add to local state instead of refreshing
        setExpenses(prevExpenses => [newExpense, ...prevExpenses]);
        setShowAddExpense(false);
      } else {
        // Fallback to refresh if we can't get the new expense
        console.warn('Could not get new expense from response, refreshing list');
        loadExpenses();
        setShowAddExpense(false);
      }
    } catch (error) {
      console.error('Failed to add expense:', error);
      throw error; // Let AddExpense component handle the error
    }
  };

  const handleImportCSV = async () => {
    setShowImportCSV(false);
    loadExpenses(); // Refresh the list to show imported expenses (CSV import adds multiple, so refresh is easier)
  };

  const handleExpenseUpdate = (updatedExpense) => {
    setExpenses(prevExpenses =>
      prevExpenses.map(expense =>
        expense.id === updatedExpense.id ? updatedExpense : expense
      )
    );
  };

  const handleExpenseDelete = (deletedExpenseId) => {
    setExpenses(prevExpenses =>
      prevExpenses.filter(expense => expense.id !== deletedExpenseId)
    );
  };

  const handleFiltersChange = async (newFilters) => {
    // Clear selected category when filters change
    setSelectedCategory(null);
    
    // If a category filter is applied, check if it's a parent category
    // and expand it to include all subcategories
    if (newFilters.category) {
      try {
        // Load hierarchical categories to check for subcategories
        const hierarchicalData = await categoryService.getCategories();
        const hierarchicalCategories = hierarchicalData.categories || [];
        
        // Find if the selected category is a parent with subcategories
        const selectedParentCategory = hierarchicalCategories.find(cat => 
          cat.name === newFilters.category && 
          cat.subcategories && 
          cat.subcategories.length > 0
        );
        
        if (selectedParentCategory) {
          // Create an array of category names including parent and all subcategories
          const categoryNames = [
            selectedParentCategory.name,
            ...selectedParentCategory.subcategories.map(sub => sub.name)
          ];
          
          // Replace the single category with the expanded list
          newFilters.categories = categoryNames;
          delete newFilters.category; // Remove the single category filter
        }
      } catch (error) {
        console.error('Failed to expand category filter:', error);
        // If there's an error, continue with the original filter
      }
    }
    
    setFilters(newFilters);
    loadExpenses(newFilters);
  };

  const handleCategorySelect = (categoryName) => {
    if (selectedCategory === categoryName) {
      // Deselect if clicking the same category
      setSelectedCategory(null);
    } else {
      // Select the new category
      setSelectedCategory(categoryName);
    }
  };

  const handleTutorialComplete = async () => {
    try {
      await updateUser({ tutorial: true });
      setShowTutorial(false);
    } catch (error) {
      console.error('Failed to update tutorial status:', error);
      // Still hide tutorial even if update fails
      setShowTutorial(false);
    }
  };

  const downloadExpensesCSV = (expensesToDownload) => {
    if (!expensesToDownload || expensesToDownload.length === 0) {
      alert('No expenses to download');
      return;
    }

    // Create CSV headers
    const headers = ['Date', 'Description', 'Amount', 'Category', 'Added By', 'Entry Method', 'Import Template'];

    // Convert expenses to CSV rows
    const csvRows = expensesToDownload.map(expense => [
      expense.date,
      `"${expense.description.replace(/"/g, '""')}"`, // Escape quotes in description
      Math.abs(expense.amount).toFixed(2),
      `"${expense.category.replace(/"/g, '""')}"`, // Escape quotes in category
      `"${expense.addedBy.replace(/"/g, '""')}"`, // Escape quotes in addedBy
      expense.entryMethod || 'manual',
      expense.importTemplate ? `"${expense.importTemplate.replace(/"/g, '""')}"` : ''
    ]);

    // Combine headers and rows
    const csvContent = [headers, ...csvRows]
      .map(row => row.join(','))
      .join('\n');

    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Generate filename with current date and filter info
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const hasFilters = Object.keys(filters).length > 0;
    const filterSuffix = hasFilters ? '_filtered' : '';
    const filename = `family_budget_expenses_${dateStr}${filterSuffix}.csv`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      {showTutorial && (
        <OnboardingSlides onComplete={handleTutorialComplete} />
      )}

      <div className="accounts-container">
        <header className="page-header">
          <div className="page-header-content">
            <h1 className="page-title">Family Budget</h1>
            <p className="page-subtitle">
              Welcome back, <span
                className="user-name"
                onClick={() => setShowChangePassword(true)}
                title="Click to change password"
              >
                {user?.name}
              </span>!{' '}
              Family: <span className="family-name">{user?.familyName}</span>
            </p>

            <nav className="page-nav">
              <button onClick={() => navigate('/home')} className="nav-btn active">
                📊 Dashboard
              </button>
              <button onClick={() => navigate('/categories')} className="nav-btn">
                📂 Categories
              </button>
              <button onClick={() => navigate('/accounts')} className="nav-btn">
                🏦 Accounts
              </button>
              <button onClick={() => navigate('/help')} className="nav-btn">
                ❓ Help
              </button>
            </nav>
          </div>
          <button onClick={logout} className="btn logout-btn">
            Logout
          </button>
        </header>

        <div className="card">
          <div style={{
            display: 'flex',
            flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: window.innerWidth <= 768 ? 'stretch' : 'center',
            gap: window.innerWidth <= 768 ? '16px' : '0',
            marginBottom: '24px'
          }}>
            <h2 style={{
              color: '#f0f0f0',
              margin: '0',
              fontSize: window.innerWidth <= 480 ? '1.4rem' : '1.8rem',
              fontWeight: '600'
            }}>
              Recent Expenses
              {selectedCategory && (
                <span style={{
                  color: '#3498db',
                  fontSize: window.innerWidth <= 480 ? '1rem' : '1.2rem',
                  fontWeight: '400',
                  marginLeft: '12px'
                }}>
                  → {selectedCategory}
                </span>
              )}
            </h2>
            <div style={{
              display: 'flex',
              gap: window.innerWidth <= 480 ? '8px' : '12px',
              flexDirection: window.innerWidth <= 480 ? 'column' : 'row'
            }}>
              <button
                onClick={() => setShowImportCSV(true)}
                className="btn"
                style={{
                  background: 'rgba(255, 107, 53, 0.2)',
                  border: '1px solid #ff6b35',
                  color: '#ff6b35',
                  boxShadow: '0 2px 8px rgba(255, 107, 53, 0.2)',
                  fontSize: window.innerWidth <= 480 ? '13px' : '14px',
                  padding: window.innerWidth <= 480 ? '10px 16px' : '12px 24px'
                }}
              >
                📤 Import CSV
              </button>
              <button
                onClick={() => setShowAddExpense(true)}
                className="btn"
                style={{
                  fontSize: window.innerWidth <= 480 ? '13px' : '16px',
                  padding: window.innerWidth <= 480 ? '10px 16px' : '12px 24px'
                }}
              >
                + Add Expense
              </button>
            </div>
          </div>

          <ExpenseFilters
            onFiltersChange={handleFiltersChange}
            currentFilters={filters}
          />

          {loading ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#b0b0b0',
              fontSize: '1.1rem'
            }}>
              Loading expenses...
            </div>
          ) : (
            <>
              <BudgetTracker expenses={expenses} />
              <SimplePieChart 
                expenses={expenses} 
                onCategorySelect={handleCategorySelect}
                selectedCategory={selectedCategory}
              />
              <ExpenseList
                expenses={expenses}
                categories={categories}
                accounts={accounts}
                onExpenseUpdate={handleExpenseUpdate}
                onExpenseDelete={handleExpenseDelete}
                onDownloadCSV={downloadExpensesCSV}
                filters={filters}
                selectedCategory={selectedCategory}
              />
            </>
          )}
        </div>

        {showAddExpense && (
          <AddExpense
            categories={categories}
            loadingCategories={loadingCategories}
            accounts={accounts}
            onAdd={handleAddExpense}
            onCancel={() => setShowAddExpense(false)}
          />
        )}

        {showImportCSV && (
          <ImportCSV
            onImport={handleImportCSV}
            onCancel={() => setShowImportCSV(false)}
          />
        )}

        {showChangePassword && (
          <ChangePassword
            onClose={() => setShowChangePassword(false)}
            onSuccess={() => {
              alert('Password changed successfully!');
            }}
          />
        )}
      </div>
    </>
  );
}

export default Home;